import { useMutation } from '@tanstack/react-query'
import { CHAIN_CONFIG, CHAIN_INFO_LIST, getDexInfoFromDenom } from '@/config'
import {
  calculateFeeWithHelper,
  deductFeeFromTxAmountWhileEmptyStrd,
  generateIbcTimeoutTimestampBigInt,
  simulate
} from '@/wallet-utils'
import { assert } from '@/utils'
import { MutationParameters, SignMutationParameters } from './types'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { useSelectedDenom } from '../../../../contexts/denom'
import { useSelectedChain, useStrideChain, useDexChain, useStrideChainBalance } from '@/contexts/wallet'
import { flushSync } from 'react-dom'
import { ibc } from 'stridejs'

const useSignWithdrawStTokenToDexMutation = ({ setWithdrawStTokenToDexRaw }: MutationParameters) => {
  const { address: strideAddress, getSigningStargateClient } = useStrideChain()

  const { address: selectedAddress } = useSelectedChain()

  const { address: dexAddress } = useDexChain()

  const [denom] = useSelectedDenom()

  const { data: strideBalances } = useStrideChainBalance()

  // Previously we directly accessed useLiquidStakeTransactionAmountQuery here instead of accepting
  // an `amount` parameter. However, for some reason, render cycles were off. When the said query
  // updates, the mutation callback in the useEffect at that point for some reason is stale.
  // You'd expect that the mutation would be updated, but it's not.
  //
  // @TODO: Make a reproduction and report this to react-query. This has got to be a more common use-case,
  // and more people are probably running into this.
  const handleMutation = async ({ amount }: SignMutationParameters): Promise<TxRaw> => {
    assert(strideAddress, 'You are unable to send token without connecting your Stride wallet.')

    assert(selectedAddress, 'You are unable to send token without connecting your selected wallet.')

    assert(dexAddress, 'You are unable to send token without connecting your dex wallet.')

    const dexInfo = getDexInfoFromDenom(denom)

    assert(dexInfo, `Missing dex info for ${denom}`)

    const minimalDenom = CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom

    const stakedMinimalDenom = `st${CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom}`

    const createMsgTransfer = (amount: string) => {
      return ibc.applications.transfer.v1.MessageComposer.withTypeUrl.transfer({
        sourcePort: 'transfer',
        sourceChannel: CHAIN_CONFIG[dexInfo.coinDenom].withdrawChannel,
        token: {
          amount,
          denom: stakedMinimalDenom
        },
        sender: strideAddress,
        receiver: dexAddress,
        memo: '',
        timeoutHeight: {
          revisionHeight: 0n,
          revisionNumber: 0n
        },
        timeoutTimestamp: generateIbcTimeoutTimestampBigInt()
      })
    }

    const client = await getSigningStargateClient()

    const gas = await simulate(client, strideAddress, [createMsgTransfer(amount)], '')
    const { fee, deductible } = calculateFeeWithHelper(minimalDenom, gas)

    // Allot a small portion of the user's tokens for the transaction gas fee
    //
    // Testing Guide (Preparation):
    // (Make sure you are testing with a chain that has "Add to DEX" enabled like Cosmos Hub or Saga)
    // 1. Setup a fresh account, and send a small amount of tokens to it (e.g., 1 SAGA).
    // 2. Liquid Stake then proceed with "Add to DEX" flow
    // 3. Proceed with "Withdraw St Token" flow.
    // 4. Inspect the transaction amount and you'll find that it should be less than the full amount.
    //
    // @TODO: Only deduct if the user does not have enough tokens for gas
    const amountWithSafetyDeduction = deductFeeFromTxAmountWhileEmptyStrd(
      strideBalances?.balances ?? [],
      BigInt(amount),
      deductible
    )

    const msgTransfer = createMsgTransfer(amountWithSafetyDeduction.toString())
    return await client.sign(strideAddress, [msgTransfer], fee, '')
  }

  const handleSuccess = (raw: TxRaw) => {
    flushSync(() => {
      setWithdrawStTokenToDexRaw(raw)
    })
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useSignWithdrawStTokenToDexMutation }
